import React from 'react';
import { StyleSheet, View } from 'react-native';
import {
  Button,
  HStack,
  Modal,
  ModalBodyText,
  ModalFooter,
  ModalHeadline,
  theme,
  useResponsive,
} from '@sweetgreen/garnish';

import { useLocalizationContext } from '@order/Localization';

import type { LocationResultData } from '../../screens/LocationScreen/GraphQL';

export const LocationWarningModal = ({
  locations,
  restaurantSlug,
  onClose,
  onConfirm,
}: LocationWarningModalProps) => {
  const location = locations.find((el) => el.restaurantSlug === restaurantSlug);

  return (
    <Modal animationType="fade" size="medium" visible onRequestClose={onClose}>
      <LocationWarningContent
        locationWarningTitle={location?.warningDialogTitle ?? ''}
        locationWarningDescription={location?.warningDialogDescription ?? ''}
        restaurantSlug={restaurantSlug}
        restaurantId={location?.id ?? ''}
        restaurantName={location?.name ?? ''}
        onClose={onClose}
        onConfirm={onConfirm}
      />
    </Modal>
  );
};

export const LocationWarningContent = ({
  locationWarningTitle,
  locationWarningDescription,
  restaurantSlug,
  restaurantId,
  restaurantName,
  onClose,
  onConfirm,
}: LocationWarningContentProps) => {
  const { t } = useLocalizationContext();
  const { currentBreakpoint, match } = useResponsive();

  const onChangeLocation = React.useCallback(() => {
    onClose();
  }, [onClose]);

  const handleConfirm = React.useCallback(() => {
    if (!onConfirm || !restaurantSlug) return;
    onConfirm({
      restaurantId: restaurantId ?? '',
      restaurantSlug: restaurantSlug ?? '',
      restaurantName: restaurantName ?? '',
    });
  }, [onConfirm, restaurantSlug, restaurantId, restaurantName]);

  // ─── Styles ──────────────────────────────────────────────────────

  const containerStyles = [
    styles.container,
    match([undefined, styles.containerSM]),
  ];
  const infoBlockStyles = match([styles.infoXS, styles.infoSM]);
  const buttonSize = match(['medium', 'large'] as const);
  const buttonStyles = match([undefined, styles.buttonSM]);

  // ─────────────────────────────────────────────────────────────────

  return (
    <View style={containerStyles} testID="location-warning-modal">
      <View style={infoBlockStyles}>
        <ModalHeadline>{locationWarningTitle}</ModalHeadline>
        <ModalBodyText>{locationWarningDescription}</ModalBodyText>
      </View>

      <ModalFooter withoutTopBorder>
        <HStack
          style={styles.buttonsStack}
          itemsPerRow={currentBreakpoint.isMobile ? 2 : undefined}
          shouldFillAvailableSpace={false}
        >
          <Button
            style={buttonStyles}
            size={buttonSize}
            palette="secondary"
            onPress={onChangeLocation}
            accessibilityLabel={t('location.warning-modal.cta-change')}
            accessibilityHint={t('location.warning-modal.cta-change')}
          >
            {t('location.warning-modal.cta-change')}
          </Button>

          <Button
            style={buttonStyles}
            size={buttonSize}
            onPress={handleConfirm}
            accessibilityLabel={t('location.warning-modal.cta-confirm')}
            accessibilityHint={t('location.warning-modal.cta-confirm')}
            testID="location-warning-modal.confirm"
          >
            {t('location.warning-modal.cta-confirm')}
          </Button>
        </HStack>
      </ModalFooter>
    </View>
  );
};

type LocationWarningModalProps = {
  readonly locations: readonly LocationResultData[];
  readonly restaurantSlug: string;
  readonly onClose: () => void;
  readonly onConfirm: (props: {
    readonly restaurantId: string;
    readonly restaurantSlug: string;
    readonly restaurantName: string;
  }) => void;
};

type LocationWarningContentProps = {
  readonly locationWarningTitle: string;
  readonly locationWarningDescription: string;
  readonly restaurantSlug: string;
  readonly restaurantId: string;
  readonly restaurantName: string;
  readonly onClose: () => void;
  readonly onConfirm: (props: {
    readonly restaurantId: string;
    readonly restaurantSlug: string;
    readonly restaurantName: string;
  }) => void;
};

const styles = StyleSheet.create({
  container: {
    paddingTop: theme.spacing.small,
    paddingBottom: theme.spacing.small,
  },
  containerSM: {
    paddingTop: theme.spacing.large,
  },
  infoXS: {
    marginTop: theme.spacing.medium,
  },
  infoSM: {
    marginVertical: theme.spacing.medium,
  },
  buttonsStack: {
    justifyContent: 'center',
  },
  buttonSM: {
    minWidth: 187,
    paddingHorizontal: theme.spacing.small,
  },
});
